import React, { useEffect, useState } from "react";
import * as api from "../../../../backend/request";
import { useGetForms, useGetUserCompanies } from "../../../../backend/query";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { AxiosError } from "axios";
import {
  validateFileWithContent,
  FILE_TYPE_GROUPS,
  FILE_SIZE_LIMITS,
  generateAcceptAttribute,
  VALIDATION_MESSAGES,
} from "../../../../utils/fileValidation";

const VTB_STYLES = `
.vtb-backdrop {
  position: fixed; inset: 0;
  background: rgba(15,23,42,0.45);
  z-index: var(--z-backdrop);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.vtb-modal {
  background: #fff;
  border: 1px solid #E6E6E6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 560px; max-width: 100%;
  max-height: calc(100vh - 32px);
  display: flex; flex-direction: column;
  font-family: 'Montserrat', sans-serif;
  overflow: hidden;
}
.vtb-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; flex-shrink: 0;
  border-bottom: 1px solid #E6E6E6;
}
.vtb-title { font-size: 18px; font-weight: 600; color: #1E1E1E; font-family: 'Montserrat', sans-serif; }
.vtb-close {
  background: none; border: none; cursor: pointer;
  padding: 4px; color: #1E1E1E;
  display: inline-flex; align-items: center; justify-content: center;
}
.vtb-close:hover { opacity: 0.7; }
.vtb-body {
  padding: 20px 24px; overflow-y: auto; flex: 1;
  display: flex; flex-direction: column; gap: 16px;
}
.vtb-field-label {
  display: block;
  font-size: 14px; font-weight: 500; color: #1E1E1E;
  font-family: 'Montserrat', sans-serif;
  margin-bottom: 6px;
}
.vtb-file-input {
  display: block; width: 100%;
  border: 1px solid #D1D5DB; border-radius: 8px;
  padding: 8px 12px; box-sizing: border-box;
  font-family: 'Montserrat', sans-serif; font-size: 14px;
  cursor: pointer;
}
.vtb-file-list-wrap { max-height: 220px; overflow-y: auto; overflow-x: hidden; border: 1px solid #E6E6E6; border-radius: 8px; }
.vtb-table {
  width: 100%; border-collapse: collapse;
  font-size: 14px; font-family: 'Montserrat', sans-serif;
}
.vtb-table th {
  text-align: left; font-weight: 600; color: #1E1E1E;
  padding: 8px 12px; background: #F9FAFB;
  font-size: 13px; position: sticky; top: 0;
}
.vtb-table td { padding: 10px 12px; border-top: 1px solid #F3F3F3; vertical-align: middle; color: #1E1E1E; }
.vtb-empty-cell { text-align: center; color: #6b7280; padding: 20px 12px !important; }
.vtb-delete-btn {
  background: none; border: none; cursor: pointer;
  color: #C00F0C; opacity: 0.6; padding: 2px;
  display: inline-flex; align-items: center;
}
.vtb-delete-btn:hover { opacity: 1; }
.vtb-error { font-size: 12px; color: #C00F0C; margin-top: 2px; }
.vtb-hint { font-size: 13px; color: #6b7280; font-family: 'Montserrat', sans-serif; margin: 0; }
.vtb-foot {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; flex-shrink: 0;
  border-top: 1px solid #E6E6E6;
}
.vtb-foot-right { display: flex; gap: 8px; }
.vtb-back-btn {
  height: 38px; padding: 0 16px; border-radius: 8px;
  border: 1px solid #D1D5DB; background: #fff;
  font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 500;
  color: #1E1E1E; cursor: pointer;
}
.vtb-back-btn:hover { border-color: #9CA3AF; }
.vtb-reset-btn {
  height: 38px; padding: 0 16px; border-radius: 8px;
  border: 1px solid #D1D5DB; background: #F3F4F6;
  font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 500;
  color: #1E1E1E; cursor: pointer;
}
.vtb-reset-btn:hover { background: #E5E7EB; }
.vtb-submit-btn {
  height: 38px; padding: 0 16px; border-radius: 8px;
  border: none; background: #2965AD;
  font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 500;
  color: #fff; cursor: pointer;
}
.vtb-submit-btn:hover:not(:disabled) { background: #1e4f8a; }
.vtb-submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }
`;

function VendorBulkUploadModalV2({ onClose, selectedItems, isVendor }: any) {
  const [submitting, setSubmitting] = useState(false);
  const [uploadFiles, setUploadedFiles] = useState<any>([]);
  const [payload, setPayload] = useState<any>({});
  const { forms, isFetching: fetchingForms } = useGetForms(
    payload,
    Boolean(payload.location) && Boolean(payload.month) && Boolean(payload.year)
  );

  function validateFormSelection() {
    const _uploadedFiles = [...uploadFiles];
    _uploadedFiles.forEach((file) => {
      file.type = null;
      file.duplicate = false;
      file.required = false;
    });
    setUploadedFiles(_uploadedFiles);
  }

  async function onFileChange(event: any) {
    const _uploadedFiles = [...uploadFiles];
    if (event) {
      const length = event.target.files.length;
      const time = new Date().getTime();
      for (let i = 0; i < length; i++) {
        const file = event.target.files[i];
        const validation = await validateFileWithContent(
          file,
          FILE_TYPE_GROUPS.ALL_DOCUMENTS,
          FILE_SIZE_LIMITS.LARGE
        );
        _uploadedFiles.push({
          id: time + i,
          file,
          invalidFile: !validation.isValid,
          errorMessage: validation.error,
        });
      }
      setUploadedFiles(_uploadedFiles);
      event.target.value = null;
    }
  }

  function onDeleteFile(index: number) {
    const _uploadedFiles = [...uploadFiles];
    _uploadedFiles.splice(index, 1);
    setUploadedFiles(_uploadedFiles);
  }

  function clearAll() {
    setUploadedFiles([]);
  }

  function validateAndSubmit() {
    let hasError = false;
    const _uploadedFiles = [...uploadFiles];
    setUploadedFiles(_uploadedFiles);
    if (hasError) {
      return;
    }
    setSubmitting(true);
    if (selectedItems && selectedItems.length >= 1) {
      submit(selectedItems.map((s: any) => s.id));
    }
  }

  function submit(todosToInsert: any[]) {
    const _uploadedFiles = [...uploadFiles];
    const activity = selectedItems[0];
    const formData = new FormData();
    _uploadedFiles.forEach((file) => {
      formData.append("files[]", file.file);
    });
    todosToInsert.forEach((todo) => {
      if (isVendor) {
        formData.append("todo_vendor_ids[]", todo as string);
      } else {
        formData.append("todo_ids[]", todo as string);
      }
    });
    formData.append("host", "aws");
    ["company_id", "associate_company_id", "location_id"].forEach((key) => {
      formData.append(key, activity[key]);
    });
    if (isVendor) {
      ["vendor_category_id", "vendor_registration_id"].forEach((key) => {
        formData.append(key, activity[key]);
      });
    }
    api
      .post(`/api/v1/${isVendor ? "todo_vendors" : "todos"}/upload`, formData)
      .then(
        (response: any) => {
          if (response instanceof AxiosError) {
            const data = (response.response || {}).data;
            const error = (data || {}).error || "File upload failed.";
            toast.error(error);
          } else {
            onClose(true);
            toast.success("Files Uploaded Successfully to Pending Activities");
          }
        },
        (error: any) => {
          const data = (error && error.response && error.response.data) || {};
          toast.error(data.error || data.message || 'File upload failed.');
          const details = data.details;
          if (details && details.length > 0) {
            details.forEach(function(detail: string) { toast.error(detail); });
          }
        }
      )
      .finally(() => setSubmitting(false));
  }

  useEffect(() => {
    if (!fetchingForms && forms) {
      validateFormSelection();
    }
  }, [fetchingForms]);

  return (
    <>
      <style>{VTB_STYLES}</style>
      <div className="vtb-backdrop">
        <div className="vtb-modal" role="dialog" aria-modal="true">
          <div className="vtb-head">
            <span className="vtb-title">Bulk Upload</span>
            <button
              type="button"
              className="vtb-close"
              onClick={onClose}
              aria-label="Close"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="vtb-body">
            <div>
              <label className="vtb-field-label">Select file to upload</label>
              <input
                type="file"
                className="vtb-file-input"
                onChange={onFileChange}
                accept={generateAcceptAttribute(FILE_TYPE_GROUPS.ALL_DOCUMENTS)}
                multiple
              />
              <p className="vtb-hint" style={{ marginTop: 6 }}>{VALIDATION_MESSAGES.ALLOWED_FILE_ALL_DOCS}</p>
            </div>

            <div className="vtb-file-list-wrap">
              <table className="vtb-table">
                <thead>
                  <tr>
                    <th style={{ width: "8%" }}>#</th>
                    <th style={{ width: "84%" }}>File Name</th>
                    <th style={{ width: "8%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {uploadFiles.length === 0 && (
                    <tr>
                      <td colSpan={3} className="vtb-empty-cell">
                        No files uploaded
                      </td>
                    </tr>
                  )}
                  {uploadFiles.map((file: any, index: number) => (
                    <tr key={file.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div style={{ wordBreak: 'break-all' }}>{file.file.name}</div>
                        {file.invalidFile && (
                          <div className="vtb-error">
                            {file.errorMessage ||
                              VALIDATION_MESSAGES.INVALID_FILE_ALL_DOCS}
                          </div>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="vtb-delete-btn"
                          title="Delete"
                          onClick={() => onDeleteFile(index)}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14H6L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4h6v2" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="vtb-hint">
              Activities which are in Activity Saved, Pending Mode only will get Uploaded
            </p>
          </div>

          <div className="vtb-foot">
            <button type="button" className="vtb-back-btn" onClick={onClose}>
              Back
            </button>
            <div className="vtb-foot-right">
              <button type="button" className="vtb-reset-btn" onClick={clearAll}>
                Reset
              </button>
              <button
                type="button"
                className="vtb-submit-btn"
                onClick={validateAndSubmit}
                disabled={uploadFiles.length === 0 || uploadFiles.some((f: any) => f.invalidFile)}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
      {submitting && <PageLoader />}
    </>
  );
}

export default VendorBulkUploadModalV2;
